import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  UploadCloud, Search, RefreshCw, Trash2, Copy, Check, X, Loader2,
  Image as ImageIcon, Film, FileText, File as FileIcon, Pencil, Filter,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input } from './shared';
import { useToast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

/**
 * AdminMedia — real media library.
 *
 * Lists everything in db.media_assets (every /api/admin/upload + every AI
 * image now records here). Grid of thumbnails with:
 *   * drag/drop or click upload (multi-file)
 *   * search by filename / title / alt / tags
 *   * type filter (image / video / pdf / other)
 *   * per-item: copy URL, edit alt+title+tags, delete
 *
 * URLs are stored relative ("/api/uploads/...") so they work across
 * prod + dev; we prefix BACKEND only when rendering a preview.
 */
function kindOf(mime = '', url = '') {
  const m = (mime || '').toLowerCase();
  const u = (url || '').toLowerCase();
  if (m.startsWith('image') || /\.(png|jpe?g|webp|gif|svg)$/.test(u)) return 'image';
  if (m.startsWith('video') || /\.(mp4|webm|mov)$/.test(u)) return 'video';
  if (m.includes('pdf') || u.endsWith('.pdf')) return 'pdf';
  return 'other';
}

function fmtBytes(n) {
  if (!n || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const TYPE_FILTERS = [
  { key: 'all', label: 'All', Icon: Filter },
  { key: 'image', label: 'Images', Icon: ImageIcon },
  { key: 'video', label: 'Videos', Icon: Film },
  { key: 'pdf', label: 'PDFs', Icon: FileText },
  { key: 'other', label: 'Other', Icon: FileIcon },
];

export default function AdminMedia() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [editing, setEditing] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin-v2/media', { params: { limit: 500 } });
      setItems(res.data?.items || []);
    } catch (err) {
      toast({ title: 'Failed to load media', description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((m) => {
      const k = kindOf(m.mime_type, m.url);
      if (type !== 'all' && k !== type) return false;
      if (!q) return true;
      return [m.filename, m.original_filename, m.title, m.alt_text, (m.tags || []).join(' ')]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [items, query, type]);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0;
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', 'library');
        await api.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok += 1;
      }
      toast({ title: `Uploaded ${ok} file${ok === 1 ? '' : 's'}` });
      load();
    } catch (err) {
      toast({ title: 'Upload failed', description: formatApiError(err) });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title || m.filename}"? The DB record is removed; the file stays on disk.`)) return;
    try {
      await api.delete(`/api/admin-v2/media/${m.id}`);
      setItems((arr) => arr.filter((x) => x.id !== m.id));
      toast({ title: 'Deleted' });
    } catch (err) {
      toast({ title: 'Delete failed', description: formatApiError(err) });
    }
  };

  const saveMeta = async () => {
    if (!editing) return;
    try {
      await api.put(`/api/admin-v2/media/${editing.id}`, {
        title: editing.title || '',
        alt_text: editing.alt_text || '',
        tags: typeof editing.tags === 'string'
          ? editing.tags.split(',').map((t) => t.trim()).filter(Boolean)
          : (editing.tags || []),
      });
      toast({ title: 'Saved' });
      setEditing(null);
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: formatApiError(err) });
    }
  };

  const copyUrl = async (m) => {
    const full = (m.url || '').startsWith('http') ? m.url : `${window.location.origin}${m.url}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopiedId(m.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (_) { /* ignore */ }
  };

  const previewSrc = (m) => ((m.url || '').startsWith('http') ? m.url : `${BACKEND}${m.url}`);

  return (
    <>
      <PageHeader
        title={<span className="flex items-center gap-2"><ImageIcon size={18} /> Media Library</span>}
        subtitle={`${items.length} assets · images, videos, PDFs — uploaded or AI-generated`}
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><UploadCloud size={14} /> Upload</>}
            </Btn>
            <input ref={fileRef} type="file" multiple hidden accept="image/*,video/*,application/pdf" onChange={(e) => uploadFiles(e.target.files)} />
          </div>
        }
      />

      <AdminCard>
        {/* Drop zone + filters */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); uploadFiles(e.dataTransfer.files); }}
          className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/50 hover:bg-violet-50/40 hover:border-violet-300 transition-colors p-6 text-center mb-5 cursor-pointer"
          onClick={() => fileRef.current?.click()}
        >
          <UploadCloud size={24} className="mx-auto text-violet-500 mb-2" />
          <p className="text-sm font-semibold text-slate-700">Drop files here or click to upload</p>
          <p className="text-xs text-slate-500 mt-0.5">Images, videos and PDFs · up to 25 MB each</p>
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search filename, title, alt text or tags…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none text-white text-sm placeholder:text-slate-500"
            />
          </div>
          <div className="inline-flex flex-wrap gap-1">
            {TYPE_FILTERS.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setType(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${
                  type === key ? 'bg-violet-500 text-white' : 'bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10'
                }`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Loading /> : filtered.length === 0 ? (
          <Empty>No media matches these filters. Upload something to get started.</Empty>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {filtered.map((m) => {
              const k = kindOf(m.mime_type, m.url);
              return (
                <div key={m.id} className="group rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                  <div className="aspect-square bg-slate-800 relative flex items-center justify-center overflow-hidden">
                    {k === 'image' ? (
                      <img src={previewSrc(m)} alt={m.alt_text || m.filename} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.opacity = 0.2; }} />
                    ) : k === 'video' ? (
                      <video src={previewSrc(m)} className="w-full h-full object-cover" muted preload="metadata" />
                    ) : k === 'pdf' ? (
                      <FileText size={36} className="text-rose-300" />
                    ) : (
                      <FileIcon size={36} className="text-slate-400" />
                    )}
                    {/* Hover actions */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                      <button onClick={() => copyUrl(m)} title="Copy URL" className="w-8 h-8 rounded-lg bg-white/90 text-slate-900 flex items-center justify-center hover:bg-white">
                        {copiedId === m.id ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                      </button>
                      <button onClick={() => setEditing({ ...m, tags: (m.tags || []).join(', ') })} title="Edit" className="w-8 h-8 rounded-lg bg-white/90 text-slate-900 flex items-center justify-center hover:bg-white">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(m)} title="Delete" className="w-8 h-8 rounded-lg bg-rose-500 text-white flex items-center justify-center hover:bg-rose-600">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-[11px] font-semibold text-white truncate" title={m.title || m.filename}>{m.title || m.filename}</p>
                    <p className="text-[10px] text-slate-400">{k} · {fmtBytes(m.size_bytes)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AdminCard>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setEditing(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-md bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">Edit asset</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/10 bg-slate-800 mb-4 aspect-video flex items-center justify-center">
              {kindOf(editing.mime_type, editing.url) === 'image' ? (
                <img src={previewSrc(editing)} alt="" className="w-full h-full object-contain" />
              ) : kindOf(editing.mime_type, editing.url) === 'video' ? (
                <video src={previewSrc(editing)} controls className="w-full h-full" />
              ) : (
                <FileText size={40} className="text-slate-400" />
              )}
            </div>

            <div className="space-y-3">
              <Input label="Title" value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <Input label="Alt text (SEO + accessibility)" value={editing.alt_text || ''} onChange={(e) => setEditing({ ...editing, alt_text: e.target.value })} />
              <Input label="Tags (comma separated)" value={editing.tags || ''} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">URL</p>
                <div className="flex gap-2">
                  <input readOnly value={editing.url} className="flex-1 px-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 text-slate-300 text-xs font-mono" />
                  <Btn size="sm" variant="ghost" onClick={() => copyUrl(editing)}><Copy size={12} /></Btn>
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-2 pt-4 border-t border-white/10">
              <Btn onClick={saveMeta}><Check size={14} /> Save</Btn>
              <Btn variant="danger" onClick={() => { remove(editing); setEditing(null); }}><Trash2 size={14} /> Delete</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
